import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listarW1, atualizarW1 } from "../api";
import W1EditarModal from "../popup/W1EditarModal";
import styles from "./page.module.css";

// ✅ usa util que respeita o fuso do backend (X-Timezone) e não “anda” a data
import { toKeyYYYYMMDD } from "../../../utils/datesBR";

/* ====== Formatadores seguros ====== */
const fmtDate = (v) => {
  const k = toKeyYYYYMMDD(v);                 // "YYYY-MM-DD" (seguro)
  if (!k) return "";
  const [y, m, d] = k.split("-");
  return `${d}/${m}/${y}`;                    // "DD/MM/YYYY"
};

// Mostra o que veio do banco, mas se for "1.000" → "1", "1.50" → "1.5"
const fmtQtd = (v) => {
  if (v == null) return "";
  const s = String(v);
  // Se for decimal textual, remove zeros à direita e ponto final, preservando “como no banco”
  // (MySQL DECIMAL costuma vir como "1.000"). Isso deixa "1".
  if (/^\d+\.\d+$/.test(s)) {
    return s.replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "").replace(/\.$/, "");
  }
  return s;
};

// "YYYY-MM-DD HH:mm[:ss]" → "DD/MM/YYYY HH:mm"
// (middleware já devolve local em string; não vamos criar Date)
const fmtDateTime = (s) => {
  if (!s) return "";
  const m = String(s).match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
  );
  if (!m) return String(s);
  return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
};

export default function W1EdicaoPage({ onBack }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const data = await listarW1({ limit: 500 });
      setRows(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      [
        r.codigo_unico,
        toKeyYYYYMMDD(r.data), // busca também por data
        r.gerencia,
        r.coordenador,
        r.atividade,
        r.unidade_de_medida,
        r.ativo,
        r.equipamento,
        r.sub,
        r.os,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, q]);

  async function handleSave(patch) {
    if (!sel) return;
    const id = sel.id;
    const out = await atualizarW1(id, patch);
    if (out?.ok && out.data) {
      const newRow = out.data;
      setRows((list) => list.map((r) => (r.id === id ? { ...r, ...newRow } : r)));
      setSel(null);
    } else {
      alert("Erro ao salvar a edição.");
    }
  }

  function goBack() {
    if (typeof onBack === "function") return onBack();
    navigate("/"); // fallback
  }

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.left}>
          <button className={styles.backBtn} onClick={goBack} title="Voltar">
            ← Voltar
          </button>
          <h2 className={styles.title}>Programações (WF-1) — Edição</h2>
        </div>

        <div className={styles.actions}>
          <input
            className={styles.search}
            placeholder="Buscar por código, atividade, gerência, OS..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button className={styles.reload} onClick={load} disabled={loading}>
            {loading ? "Atualizando..." : "Recarregar"}
          </button>
        </div>
      </header>

      <div className={styles.wrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              {[
                "Código",
                "Data",
                "Gerência",
                "Coordenador",
                "Atividade",
                "Un.",
                "Qtd",
                "Tempo",
                "OS",
                "Atualizado",
                "",
              ].map((h) => (
                <th key={h} className={styles.th}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className={styles.tr}>
                <td className={styles.td}>
                  <strong>{r.codigo_unico}</strong>
                </td>
                <td className={styles.td}>{fmtDate(r.data)}</td>
                <td className={styles.td}>{r.gerencia}</td>
                <td className={styles.td}>{r.coordenador}</td>
                <td className={`${styles.td} ${styles.ellipsis}`} title={r.atividade}>
                  {r.atividade}
                </td>
                <td className={styles.td}>{r.unidade_de_medida}</td>
                <td className={`${styles.td} ${styles.num}`}>{fmtQtd(r.quantidade)}</td>
                <td className={styles.td}>{r.tempo_do_intervalo}</td>
                <td className={styles.td}>{r.os}</td>
                <td className={styles.td}>{fmtDateTime(r.atualizado_em)}</td>
                <td className={styles.td}>
                  <button className={styles.edit} onClick={() => setSel(r)}>
                    Editar
                  </button>
                </td>
              </tr>
            ))}
            {!filtered.length && (
              <tr>
                <td className={styles.empty} colSpan={11}>
                  Nenhum registro
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <W1EditarModal
        open={!!sel}
        initial={sel}
        onClose={() => setSel(null)}
        onSave={handleSave}
      />
    </div>
  );
}
